import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";

import { labelFor } from "../../services/enumLabels";
import { DASH, figureDir, fmtLocale, formatCell, plotValue } from "./reportFormat";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Filler, Tooltip, Legend);

const VARS = {
  accent: "--pl-accent",
  accentWash: "--pl-accent-wash",
  ink: "--pl-text",
  ink2: "--pl-text-2",
  line: "--pl-line",
  surface: "--pl-surface",
  info: "--pl-info",
  completed: "--pl-st-completed",
  cancelled: "--pl-st-cancelled",
  no_show: "--pl-st-no-show",
};

const readColors = () => {
  const style = getComputedStyle(document.documentElement);

  return Object.fromEntries(
    Object.entries(VARS).map(([key, name]) => [key, style.getPropertyValue(name).trim()])
  );
};

/** Design-token colours, re-read when the theme class on <html> flips. */
function useThemeColors() {
  const [colors, setColors] = useState(readColors);

  useEffect(() => {
    const observer = new MutationObserver(() => setColors(readColors()));
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class", "data-theme"],
    });

    return () => observer.disconnect();
  }, []);

  return colors;
}

/** "#0fa97b" → "rgba(15,169,123,a)" for the soft area fill. */
const withAlpha = (hex, alpha) => {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex || "");
  if (!match) return hex;
  const n = Number.parseInt(match[1], 16);

  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
};

/**
 * The charts row (spec 02 §4): one or two charts as the definition declares.
 *
 * With "All currencies" a money chart's series come once per currency; each
 * currency gets its own panel, never one summed axis (decision 2). Clicking a
 * point or bar opens the drilldown for that point (AC01).
 */
export default function ReportCharts({ charts = [], onPoint }) {
  // A chart with `measures` (e.g. collections / visits) shows one at a time.
  const [measureOf, setMeasureOf] = useState({});

  const shown = useMemo(
    () =>
      charts.map((chart) => {
        if (!chart.measures?.length) return chart;

        const measure = chart.measures.find((m) => m.key === measureOf[chart.key]) ?? chart.measures[0];

        return {
          ...chart,
          value_type: measure.value_type,
          measure: measure.key,
          series: (chart.series ?? []).filter((s) => s.measure === measure.key),
        };
      }),
    [charts, measureOf]
  );

  const panels = useMemo(
    () =>
      shown.flatMap((chart) => {
        const currencies = [...new Set((chart.series ?? []).map((s) => s.currency ?? null))];

        if (currencies.length <= 1) return [{ chart, currency: currencies[0] ?? null, multi: false }];

        return currencies.map((currency) => ({
          chart: { ...chart, series: chart.series.filter((s) => (s.currency ?? null) === currency) },
          currency,
          multi: true,
        }));
      }),
    [shown]
  );

  if (panels.length === 0) return null;

  return (
    <div className={`grid gap-3 ${panels.length > 1 ? "xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]" : ""}`}>
      {panels.map(({ chart, currency, multi }) => (
        <div key={`${chart.key}-${currency ?? "all"}`} className="card relative min-w-0 p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <p className="text-card-title font-bold">
              {chart.title}
              {multi && currency ? (
                <span className="ms-2 text-dense font-normal text-ink-2">{currency}</span>
              ) : null}
            </p>
            {chart.measures?.length > 1 ? (
              <MeasureToggle
                measures={chart.measures}
                value={chart.measure}
                onChange={(key) => setMeasureOf((current) => ({ ...current, [chart.key]: key }))}
              />
            ) : null}
          </div>
          <ChartBody chart={chart} onPoint={onPoint} />
        </div>
      ))}
    </div>
  );
}

/** Segmented control between a chart's measures. */
function MeasureToggle({ measures, value, onChange }) {
  return (
    <div className="inline-flex rounded-lg border border-line p-0.5" role="group">
      {measures.map((measure) => (
        <button
          key={measure.key}
          type="button"
          aria-pressed={measure.key === value}
          onClick={() => onChange(measure.key)}
          className={`rounded-md px-2.5 py-1 text-dense ${
            measure.key === value ? "bg-accent-wash font-bold text-accent-text" : "text-ink-2 hover:bg-raised"
          }`}
        >
          {measure.label}
        </button>
      ))}
    </div>
  );
}

/** One chart without its card — the dashboard draws it inside its own widget. */
export function ChartBody({ chart, onPoint }) {
  const { t } = useTranslation();
  const empty = (chart.series ?? []).every((s) => (s.points ?? []).length === 0);

  if (empty) {
    return <p className="py-12 text-center text-dense text-ink-2">{t("reports.shell.noData")}</p>;
  }

  if (chart.type === "hbar") return <BarList chart={chart} onPoint={onPoint} />;
  if (chart.type === "heatmap") return <Heatmap chart={chart} onPoint={onPoint} />;
  if (chart.type === "waterfall") return <Waterfall chart={chart} />;

  return <CanvasChart chart={chart} onPoint={onPoint} />;
}

/** Horizontal bars with values, as in the reference ("Collections by branch"). */
function BarList({ chart, onPoint }) {
  const { t, i18n } = useTranslation();
  const points = chart.series?.find((s) => s.name === "current")?.points ?? chart.series?.[0]?.points ?? [];
  // A target (R18 utilization) is drawn as a marker on every bar's track.
  const target = typeof chart.target === "number" ? chart.target : null;
  const max = Math.max(0, target ?? 0, ...points.map((p) => plotValue(p.y) ?? 0));
  const targetAt = target !== null && max > 0 ? (target / max) * 100 : null;

  return (
    <ul className="space-y-3">
      {targetAt !== null && (
        <li className="grid grid-cols-[minmax(0,7rem)_minmax(0,1fr)_auto] gap-3 px-1 text-caption text-ink-2">
          <span />
          <span className="relative h-4">
            <span
              className="absolute top-0 -translate-x-1/2 whitespace-nowrap rtl:translate-x-1/2"
              style={{ insetInlineStart: `${targetAt}%` }}
            >
              {t("reports.shell.target", {
                value: formatCell(target, chart.value_type ?? "percent", i18n.language, t),
              })}
            </span>
          </span>
          <span />
        </li>
      )}
      {points.map((point) => {
        const value = plotValue(point.y) ?? 0;
        const width = max > 0 ? Math.max(2, (value / max) * 100) : 0;

        return (
          <li key={point.point ?? point.x}>
            <button
              type="button"
              disabled={!point.point || !onPoint}
              onClick={() => onPoint?.(point.point, point.x)}
              className="grid w-full grid-cols-[minmax(0,7rem)_minmax(0,1fr)_auto] items-center gap-3 rounded-lg px-1 py-1 text-start text-dense hover:bg-raised disabled:hover:bg-transparent"
              title={t("reports.shell.openDetail")}
            >
              <span className="truncate">{point.x}</span>
              <span className="relative h-4 overflow-hidden rounded bg-raised">
                <span className="block h-full rounded bg-accent" style={{ width: `${width}%` }} />
                {targetAt !== null && (
                  <span
                    aria-hidden="true"
                    className="absolute inset-y-0 w-0.5 bg-ink"
                    style={{ insetInlineStart: `${targetAt}%` }}
                  />
                )}
              </span>
              <span className="tabular-nums" dir={figureDir(formatCell(point.y, chart.value_type ?? "money", i18n.language, t))}>
                {formatCell(point.y, chart.value_type ?? "money", i18n.language, t)}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Opening → each movement → closing (R35). `total` points are absolute bars
 * from zero; `step` points float from the running total, up or down.
 */
function Waterfall({ chart }) {
  const { t, i18n } = useTranslation();
  const points = chart.series?.[0]?.points ?? [];
  let running = 0;
  const bars = points.map((point) => {
    const value = plotValue(point.y) ?? 0;

    if (point.kind === "total") {
      running = value;
      return { point, from: 0, to: value, total: true };
    }

    const from = running;
    running += value;
    return { point, from, to: running, total: false, up: value >= 0 };
  });
  const top = Math.max(1, ...bars.map((b) => Math.max(b.from, b.to)));
  const bottom = Math.min(0, ...bars.map((b) => Math.min(b.from, b.to)));
  const span = top - bottom;
  const at = (v) => ((v - bottom) / span) * 100;

  return (
    <div className="flex h-56 items-stretch gap-2">
      {bars.map(({ point, from, to, total, up }) => (
        <div key={point.x} className="flex min-w-0 flex-1 flex-col items-center gap-1">
          <span className="text-caption tabular-nums text-ink-2">
            {formatCell(point.y, chart.value_type ?? "count", i18n.language, t)}
          </span>
          <div className="relative w-full flex-1">
            <div
              className={`absolute inset-x-1 rounded ${total ? "bg-ink-2" : up ? "bg-accent" : "bg-danger"}`}
              style={{
                bottom: `${at(Math.min(from, to))}%`,
                height: `${Math.max(0.5, at(Math.max(from, to)) - at(Math.min(from, to)))}%`,
              }}
            />
          </div>
          <span className="w-full truncate text-center text-caption text-ink-2" title={point.x}>
            {point.x}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * A grid: weekday × hour (R17) or cohort × days (R22). Points carry `row` and
 * `x`, in the server's order; the shade is the cell's share of the largest.
 * An unknown cell (`y` null, e.g. an immature cohort) is greyed with "—".
 * Rates are written in their cells.
 */
function Heatmap({ chart, onPoint }) {
  const { t, i18n } = useTranslation();
  const points = chart.series?.[0]?.points ?? [];
  const rows = [...new Set(points.map((p) => p.row))];
  const columns = [...new Set(points.map((p) => p.x))];
  const cell = new Map(points.map((p) => [`${p.row}|${p.x}`, p]));
  const max = Math.max(0, ...points.map((p) => plotValue(p.y) ?? 0));

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-separate border-spacing-0.5 text-caption">
        <thead>
          <tr>
            <th />
            {columns.map((column) => (
              <th key={column} className="px-0.5 font-normal text-ink-2" dir={figureDir(column)}>
                {/^\d{2}:\d{2}$/.test(column) ? column.slice(0, 2) : column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row}>
              <th className="whitespace-nowrap pe-2 text-start font-normal text-ink-2">{row}</th>
              {columns.map((column) => {
                const point = cell.get(`${row}|${column}`);
                const value = plotValue(point?.y) ?? 0;
                const unknown = Boolean(point) && plotValue(point.y) === null;
                const shown = unknown
                  ? DASH
                  : formatCell(point?.y ?? 0, chart.value_type ?? "count", i18n.language, t);
                const labelled = chart.value_type === "percent";

                return (
                  <td key={column} className="p-0">
                    <button
                      type="button"
                      disabled={!value || !point?.point || !onPoint}
                      onClick={() => onPoint?.(point.point, `${row} ${column}`)}
                      title={`${row} ${column}: ${shown}`}
                      aria-label={`${row} ${column}: ${shown}`}
                      className={`relative block h-7 w-full rounded bg-raised ${labelled ? "min-w-[3.5rem]" : "min-w-[1.75rem]"} ${unknown ? "opacity-50" : ""}`}
                    >
                      <span
                        className="block h-full w-full rounded bg-accent"
                        style={{ opacity: max > 0 && value > 0 ? 0.15 + 0.85 * (value / max) : 0 }}
                      />
                      {(labelled || unknown) && (
                        <span
                          className="absolute inset-0 flex items-center justify-center tabular-nums"
                          dir={figureDir(shown)}
                        >
                          {shown}
                        </span>
                      )}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-2 flex items-center justify-end gap-2 text-caption text-ink-2">
        <span>{t("reports.shell.heatmapLow")}</span>
        <span className="h-2 w-16 rounded bg-gradient-to-r from-raised to-accent rtl:bg-gradient-to-l" />
        <span>{t("reports.shell.heatmapHigh")}</span>
      </div>
    </div>
  );
}

function CanvasChart({ chart, onPoint }) {
  const { t, i18n } = useTranslation();
  const colors = useThemeColors();
  const locale = i18n.language;
  const rtl = i18n.dir(locale) === "rtl";

  const current = chart.series.filter((s) => s.name !== "previous");
  const axis = (current[0] ?? chart.series[0]).points;
  // Day / month buckets ("2026-09-01", "2026-09") as dates; anything else as sent.
  const labels = axis.map((p) => formatCell(p.x, "text", locale, t));

  const palette = {
    current: colors.accent,
    previous: colors.ink2,
    completed: colors.completed,
    cancelled: colors.cancelled,
    no_show: colors.no_show,
    // Dashboard (spec 07): capacity booked vs still free; new vs returning.
    booked: colors.accent,
    free: colors.line,
    new_clients: withAlpha(colors.accent, 0.45),
    returning_clients: colors.accent,
  };
  const colorOf = (name, index) => palette[name] ?? [colors.accent, colors.info, colors.ink2][index % 3];

  const seriesLabel = (s) => labelFor("report_series", s.name);

  const datasets = chart.series.map((s, index) => {
    const color = colorOf(s.name, index);
    const base = {
      label: seriesLabel(s),
      data: s.points.map((p) => plotValue(p.y)),
      points: s.points,
      borderColor: color,
      backgroundColor: color,
    };

    if (chart.type === "line") {
      const previous = s.name === "previous" || s.dashed;

      return {
        ...base,
        borderDash: previous ? [6, 4] : undefined,
        borderWidth: previous ? 1.5 : 2.5,
        pointRadius: previous ? 0 : 3,
        pointHoverRadius: 5,
        tension: 0.3,
        fill: !previous,
        backgroundColor: previous ? color : withAlpha(color, 0.12),
        spanGaps: false,
      };
    }

    return {
      ...base,
      borderRadius: 4,
      maxBarThickness: 28,
      stack: chart.type === "stacked" ? "all" : undefined,
    };
  });

  const valueTick = (value) =>
    chart.value_type === "money"
      ? new Intl.NumberFormat(fmtLocale(locale), { notation: "compact" }).format(value)
      : new Intl.NumberFormat(fmtLocale(locale)).format(value);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    onHover: (event, elements) => {
      if (event.native?.target)
        event.native.target.style.cursor = elements.length && onPoint ? "pointer" : "default";
    },
    onClick: (event, elements) => {
      if (!onPoint || elements.length === 0) return;
      const index = elements[0].index;
      const point = axis[index];
      if (point?.point) onPoint(point.point, labels[index]);
    },
    plugins: {
      legend: {
        display: chart.series.length > 1,
        position: "bottom",
        rtl,
        labels: { color: colors.ink2, boxWidth: 10, boxHeight: 10, usePointStyle: true },
      },
      tooltip: {
        rtl,
        callbacks: {
          label: (ctx) => {
            const p = ctx.dataset.points?.[ctx.dataIndex];
            const shown = formatCell(p?.y ?? null, chart.value_type ?? "money", locale, t);
            const day = p && p.x !== axis[ctx.dataIndex]?.x ? ` (${formatCell(p.x, "text", locale, t)})` : "";

            return `${ctx.dataset.label}: ${shown}${day}`;
          },
        },
      },
    },
    scales: {
      x: {
        reverse: rtl,
        stacked: chart.type === "stacked",
        grid: { display: false },
        ticks: { color: colors.ink2, maxRotation: 0, autoSkipPadding: 12 },
        border: { color: colors.line },
      },
      y: {
        position: rtl ? "right" : "left",
        stacked: chart.type === "stacked",
        beginAtZero: true,
        grid: { color: colors.line },
        ticks: {
          color: colors.ink2,
          callback: valueTick,
          precision: chart.value_type === "count" ? 0 : undefined,
        },
        border: { display: false },
      },
    },
  };

  const Component = chart.type === "line" ? Line : Bar;
  const drillable = onPoint
    ? axis.map((p, i) => ({ point: p?.point, label: labels[i] })).filter((p) => p.point)
    : [];

  return (
    <>
      {/* Chart.js sizes the canvas from this box: it needs its own relative, shrinkable parent. */}
      <div className="relative h-56 w-full min-w-0">
        <Component data={{ labels, datasets }} options={options} />
      </div>
      {/* The canvas is mouse-only: the same points as buttons for the keyboard (gate 9), shown while focused. */}
      {drillable.length > 0 && (
        <ul
          aria-label={t("reports.shell.chartPoints", { title: chart.title })}
          className="sr-only focus-within:not-sr-only focus-within:mt-2 focus-within:flex focus-within:flex-wrap focus-within:gap-1"
        >
          {drillable.map((p) => (
            <li key={p.point}>
              <button
                type="button"
                onClick={() => onPoint(p.point, p.label)}
                className="rounded-md border border-line px-2 py-0.5 text-caption text-ink-2 hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                aria-label={`${p.label} — ${t("reports.shell.openDetail")}`}
              >
                {p.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
